/**
 * SelectedToolsList Component
 *
 * Display list of selected tools with remove capability
 */

import React from "react";
import { useI18n } from "@/i18n";

export interface SelectedToolsListProps {
  selectedTools: string[];
  onRemove: (toolName: string) => void;
  emptyMessage?: string;
  getDisplayName?: (toolName: string) => string; // Enhancement helper
  invalidTools?: Set<string>; // Tools that no longer exist in the system
}

export const SelectedToolsList: React.FC<SelectedToolsListProps> = ({
  selectedTools,
  onRemove,
  emptyMessage,
  getDisplayName,
  invalidTools,
}) => {
  const { messages } = useI18n();
  const t = messages.agentConfig.toolsBrowser;
  const tExtended = t as Record<string, string> | undefined;
  const defaultEmptyMessage = tExtended?.noToolsSelected || "No tools selected";

  if (selectedTools.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 text-sm">
        {emptyMessage || defaultEmptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {selectedTools.map((toolName) => {
        const displayName = getDisplayName?.(toolName) || toolName;
        const hasEnhancement = displayName !== toolName;
        const isInvalid = invalidTools?.has(toolName);

        return (
          <div
            key={toolName}
            className={`flex items-center justify-between p-2 rounded ${
              isInvalid
                ? "bg-red-50 border border-red-300"
                : "bg-blue-50 border border-blue-200"
            }`}
          >
            <div className="min-w-0 flex-1">
              <span
                className={`text-sm font-medium block truncate ${
                  isInvalid ? "text-red-700 line-through" : "text-gray-900"
                }`}
              >
                {displayName}
              </span>
              {hasEnhancement && (
                <span
                  className={`text-xs font-mono block truncate ${
                    isInvalid ? "text-red-400" : "text-gray-400"
                  }`}
                >
                  {toolName}
                </span>
              )}
              {isInvalid && (
                <span className="text-xs text-red-500 mt-0.5 block">
                  {tExtended?.toolNotFoundRemove ||
                    "Tool not found - please remove"}
                </span>
              )}
            </div>
            <button
              onClick={() => onRemove(toolName)}
              className="text-red-600 hover:text-red-800 transition-colors flex-shrink-0 ml-2"
              aria-label={`Remove ${toolName}`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
};
